import React, { useEffect, useState } from "react";
import Select from "react-select";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import Http from "../../../Common/Http";
import DatatableFunc from "../../../Common/DatatableFunc";
import Alert from "../../../Common/Alert";
import { ProgressSpinner } from "primereact/progressspinner";

const ExpenseCategoriesLedger = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [transactionData, setTransactionData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState({});
  const [categoryInfoLoading, setCategoryInfoLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const categoryId = useParams().id || selectedCategoryId;

  const getCurrentMonthDates = () => {
    const today = new Date();
    // Get first day of current month
    const firstDayOfCurrentMonth = new Date(
      today.getFullYear(),
      today.getMonth(),
      1
    );

    const formatDate = (date) => {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${year}-${month}-${day}`; // Format as YYYY-MM-DD for HTML date inputs
    };

    return {
      firstDate: formatDate(firstDayOfCurrentMonth), // First day of current month in YYYY-MM-DD format
      todayDate: formatDate(today), // Today's date in YYYY-MM-DD format
    };
  };

  const fetchCategoryDetails = async (id) => {
    setCategoryInfoLoading(true);
    try {
      const res = await Http.get(`/accounts/expense-categories/${id}`);
      setSelectedCategory(res.data.data);
    } catch (error) {
      console.error("Category details fetch error:", error);
    } finally {
      setCategoryInfoLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      // Fetch expense categories
      const resCategories = await Http.get(`/accounts/expense-categories`);
      setCategoriesData(resCategories.data);

      // Fetch transactions for selected category if categoryId exists
      if (categoryId) {
        const res = await Http.get(
          `/accounts/get-expense-category-ledger-details/${categoryId}?start_date=${startDate}&end_date=${endDate}`
        );
        setTransactionData(res.data.transactions || []);
      }

      const resRole = await Http.get(`/staff-type-check`);
      setRole(resRole.data);
    } catch (error) {
      console.error("Fetch error:", error);
      setTransactionData([]);
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    const dates = getCurrentMonthDates();
    setStartDate(dates.firstDate);
    setEndDate(dates.todayDate);
    DatatableFunc();
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchData();
    }
  }, [startDate, endDate, categoryId]);

  useEffect(() => {
    if (categoryId) {
      fetchCategoryDetails(categoryId);
    }
  }, [categoryId]);

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const cols = [
    { field: "date", header: "Date" },
    { field: "transaction_type", header: "Transaction Type" },
    { field: "flag", header: "Flag" },
    { field: "description", header: "Description" },
    { field: "amount", header: "Amount" },
    { field: "balance_after", header: "Balance After" },
  ];

  // Helper function to parse amount strings with commas
  const parseAmount = (amount) => {
    if (!amount) return 0;
    // Remove commas from the string before parsing
    const cleanAmount =
      typeof amount === "string" ? amount.replace(/,/g, "") : amount;
    return parseFloat(cleanAmount) || 0;
  };

  const totalAmount = Array.isArray(transactionData)
    ? transactionData.reduce((sum, t) => sum + parseAmount(t.amount), 0)
    : 0;
  const totalRows = Array.isArray(transactionData) ? transactionData.length : 0;

  const selectHandleChange = async (selectedOption, actionMeta) => {
    const name = actionMeta.name;
    const value = selectedOption?.value;

    if (name === "category_id" && value) {
      try {
        setLoading(true);
        setSelectedCategoryId(value);

        // Fetch data for the selected category
        const res = await Http.get(
          `/accounts/get-expense-category-ledger-details/${value}?start_date=${startDate}&end_date=${endDate}`
        );
        setTransactionData(res.data.transactions || []);

        // Fetch category details
        fetchCategoryDetails(value);
      } catch (error) {
        console.error("Fetch error:", error);
        setTransactionData([]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">Expense Categories Ledger</h5>
          </div>

          <div className="am-pagebody">
            <Alert alert={alert} />

            <div className="card pd-20 pd-sm-40">
              <div className="row">
                <div className="col-lg" style={{ zIndex: 999 }}>
                  <div className="form-group">
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      placeholder="Select Expense Category..."
                      value={
                        categoryId && categoriesData.length > 0
                          ? categoriesData
                              .filter((category) => category.id == categoryId)
                              .map((category) => ({
                                value: category.id,
                                label: category.name,
                              }))[0] || null
                          : null
                      }
                      name="category_id"
                      options={categoriesData.map((category) => ({
                        value: category.id,
                        label: category.name,
                      }))}
                      onChange={selectHandleChange}
                    />
                  </div>
                </div>
                <div className="col-lg">
                  <div className="form-group">
                    <input
                      type="date"
                      className="form-control"
                      name="start_date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-lg">
                  <div className="form-group">
                    <input
                      type="date"
                      className="form-control"
                      name="end_date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Category Information Section */}
            <div className="card pd-20 pd-sm-40">
              <h6 className="card-body-title mg-sm-b-30">
                Category Information
              </h6>

              {categoryInfoLoading ? (
                <ProgressSpinner />
              ) : selectedCategory &&
                Object.keys(selectedCategory).length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped table-bordered">
                    <tbody>
                      <tr>
                        <th width="200">Category Name</th>
                        <td>{selectedCategory.name}</td>
                      </tr>
                      <tr>
                        <th>Status</th>
                        <td>
                          <span
                            className={`badge ${
                              selectedCategory.status === "Active"
                                ? "badge-success"
                                : "badge-secondary"
                            }`}
                          >
                            {selectedCategory.status}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <th>Created At</th>
                        <td>
                          {new Date(
                            selectedCategory.created_at
                          ).toLocaleDateString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-muted">
                  <p>Select an expense category to view its information</p>
                </div>
              )}
            </div>

            <div className="card pd-20 pd-sm-40">
              <h6 className="card-body-title mg-sm-b-30">Transactions</h6>
              <ReactDataTable
                cols={cols}
                receiveData={transactionData}
                isLoading={loading}
              />
              <div className="mt-3">
                <div className="d-flex justify-content-end">
                  <div className="bg-light border rounded shadow-sm px-3 py-2 d-inline-flex align-items-center">
                    <div className="d-flex align-items-center">
                      <span className="badge bg-primary me-2">
                        <i className="fa fa-calculator"></i>
                      </span>
                      <small className="text-muted">Total Amount</small>
                      <span className="ms-2 fw-bold text-primary">
                        {totalAmount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseCategoriesLedger;
